// Auth & module utilities — extracted from MyScripts/Utils.ts

import { hasValue, hasStringValue, hasNumericValue } from './validation';
import { isInArray } from './arrays';
import { AccessAuthorities } from '@/models';
import { App } from '@/services/app-compat';
import { murmurhash3_32_gc } from '@/utils/murmurhash';

export class ModuleTypeInfo {
  submodules: Array<string>;
  ModuleType: string;
}

let _authObj: any = null;

export function getAuthObject(updateCachedObj: boolean = false): any {
  if (hasValue(_authObj) && !updateCachedObj) {
    return _authObj;
  }
  let auth: string = localStorage.getItem("AuthToken");
  if (hasValue(auth) && auth !== "") {
    _authObj = JSON.parse(auth);
  } else {
    _authObj = null;
  }
  return _authObj;
}

export function setAuthObject(authObject: any): void {
  if (hasValue(authObject)) {
    _authObj = authObject;
    const json: string = JSON.stringify(authObject);
    localStorage.setItem("AuthToken", json);
    localStorage.setItem("AuthTokenHash", murmurhash3_32_gc(json, 1).toString());
  } else {
    _authObj = null;
    localStorage.removeItem("AuthToken");
    localStorage.setItem("AuthTokenHash", "");
  }
}

export function removeAuthObject(): void {
  setAuthObject(null);
}

export function getCurrentUserId(): number {
  const authObj = getAuthObject();
  if (hasValue(authObj)) {
    return +(authObj["user_id"]);
  } else {
    return null;
  }
}

export function getCurrentUsername(): string {
  const authObj = getAuthObject();
  if (hasValue(authObj)) {
    return authObj["username"];
  } else {
    return null;
  }
}

export function getCurrentCompanyId(): number {
  const authObj = getAuthObject();
  if (hasValue(authObj)) {
    return +(authObj["company_id"]);
  } else {
    return null;
  }
}

export function getCurrentAccessToken(): string {
  const authObj = getAuthObject();
  if (hasValue(authObj)) {
    return authObj["access_token"];
  } else {
    return null;
  }
}

export function getAvailableModules(): Array<string> {
  const authObj = getAuthObject();
  if (hasValue(authObj)) {
    return authObj["modules"];
  } else {
    return null;
  }
}

let _moduleTypeInfo: ModuleTypeInfo = null;

export function getModuleTypeInfo(): ModuleTypeInfo {
  if (hasValue(_moduleTypeInfo)) {
    return _moduleTypeInfo;
  }
  const moduleTypeInfo: string = localStorage.getItem("ModuleTypeInfo");
  if (hasValue(moduleTypeInfo) && moduleTypeInfo !== "") {
    _moduleTypeInfo = JSON.parse(moduleTypeInfo);
    return _moduleTypeInfo;
  } else {
    return null;
  }
}

export function setModuleTypeAndAvailableSubmodules(moduleTypeInfo: ModuleTypeInfo): void {
  if (hasValue(moduleTypeInfo)) {
    _moduleTypeInfo = moduleTypeInfo;
    const json: string = JSON.stringify(moduleTypeInfo);
    localStorage.setItem("ModuleTypeInfo", json);
    localStorage.setItem("ModuleTypeInfoHash", murmurhash3_32_gc(json, 1).toString());
  } else {
    _moduleTypeInfo = null;
    localStorage.removeItem("ModuleTypeInfo");
    localStorage.setItem("ModuleTypeInfoHash", "");
  }
}

export function removeModuleTypeAndAvailableSubmodules(): void {
  setModuleTypeAndAvailableSubmodules(null);
}

export function getModuleType(): string {
  let moduleTypeInfo = getModuleTypeInfo();
  if (hasValue(moduleTypeInfo)) {
    return moduleTypeInfo.ModuleType || '';
  } else {
    return '';
  }
}

export function getAvailableSubmodules(): Array<string> {
  const selectModuleResp = getModuleTypeInfo();
  if (hasValue(selectModuleResp) && hasValue(selectModuleResp.submodules)) {
    return selectModuleResp.submodules;
  } else {
    return null;
  }
}

export function hasReportApproval(): boolean {
  const submodules = getAvailableSubmodules();
  return submodules != null && submodules.find(e => e === AccessAuthorities.ROLE_REPORT_APPROVAL) !== undefined;
}

export function currentUserHasPermission(permission: string): boolean {
  return isInArray(getAvailableSubmodules(), permission);
}

export function getCompanyCountry(): string {
  const companyCountry = getAuthObject()?.companyCountry;
  return companyCountry ? companyCountry : 'US';
}

export function showUsaStateInformation(): boolean {
  return getCompanyCountry() === 'US';
}

export function getOfficerLabel(): string {
  const officerLabel = getAuthObject()?.companySettings?.officerLabel;
  return officerLabel ? officerLabel : 'Officer';
}

export function getOfficerPluralLabel(): string {
  const officerPluralLabel = getAuthObject()?.companySettings?.officerPluralLabel;
  return officerPluralLabel ? officerPluralLabel : 'Officers';
}

// Storage event listener — keeps auth/module state in sync across browser tabs
window.addEventListener("storage", event => {
  if (event.key === "ModuleTypeInfoHash") {
    const moduleTypeInfoExisted: boolean = hasValue(_moduleTypeInfo);
    _moduleTypeInfo = null;
    setTimeout(() => {
      getModuleTypeInfo();
      if (hasValue(_moduleTypeInfo)) {
        let moduleType: string = getModuleType();
        if (moduleType !== App.getModuleTypeFromURL()) {
          window.OrnaReact.navigate.toModuleHome(moduleType.toUpperCase());
        }
      } else {
        if (moduleTypeInfoExisted && hasValue(getAuthObject(true))) {
          window.OrnaReact.navigate.toHome();
        }
      }
    }, 0);
  } else if (event.key === "AuthTokenHash") {
    const authObjExisted: boolean = hasValue(_authObj);
    setTimeout(() => {
      const authObj = getAuthObject(true);
      if (hasValue(authObj)) {
        App.updatePubNubTokenFromAuthObject();
        if (hasValue(authObj["modules"]) && (authObj["modules"].length > 1) && !hasValue(_moduleTypeInfo)) {
          window.OrnaReact.navigate.toHome();
        }
      } else {
        if (authObjExisted) {
          App.signOut();
          App.showPopupNotification("Signed out due to other tab signing out.", "error");
        }
      }
    }, 0);
  }
}, false);
